/**
 * Landing Layout Configuration - Superadmin
 * Reorder and toggle landing page sections
 */
import React, { useState, useEffect } from 'react';
import { DashboardLayout } from '../../layouts/DashboardLayout';
import { getLandingLayout, updateLandingLayout } from '../../services/api';
import { ArrowUp, ArrowDown, Eye, EyeOff, Save, ExternalLink, GripVertical, RotateCcw } from 'lucide-react';
import { toast } from 'sonner';

const DEFAULT_SECTIONS = [
  { section_id: 'features', label: 'Beneficios', enabled: true, order: 1 },
  { section_id: 'banner_carousel', label: 'Banners Carrusel (3 col)', enabled: true, order: 2 },
  { section_id: 'categories', label: 'Categorias', enabled: true, order: 3 },
  { section_id: 'featured_stores', label: 'Tiendas Destacadas', enabled: true, order: 4 },
  { section_id: 'offers', label: 'Ofertas y Descuentos', enabled: true, order: 5 },
  { section_id: 'trending', label: 'Productos Populares', enabled: true, order: 6 },
  { section_id: 'new_stores', label: 'Nuevas Tiendas', enabled: true, order: 7 },
  { section_id: 'banner_row', label: 'Banners Fila (2 col)', enabled: true, order: 8 },
  { section_id: 'nearby', label: 'Tiendas Cerca de Ti', enabled: true, order: 9 },
];

const LandingLayout = () => {
  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    loadLayout();
  }, []);

  const loadLayout = async () => {
    try {
      const res = await getLandingLayout();
      const data = Array.isArray(res.data) && res.data.length > 0 ? res.data : DEFAULT_SECTIONS;
      setSections(data.sort((a, b) => a.order - b.order));
    } catch (error) {
      setSections([...DEFAULT_SECTIONS]);
    } finally {
      setLoading(false);
    }
  };

  const moveUp = (index) => {
    if (index === 0) return;
    const updated = [...sections];
    [updated[index - 1], updated[index]] = [updated[index], updated[index - 1]];
    // Reassign order values
    updated.forEach((s, i) => { s.order = i + 1; });
    setSections(updated);
    setHasChanges(true);
  };

  const moveDown = (index) => {
    if (index === sections.length - 1) return;
    const updated = [...sections];
    [updated[index], updated[index + 1]] = [updated[index + 1], updated[index]];
    updated.forEach((s, i) => { s.order = i + 1; });
    setSections(updated);
    setHasChanges(true);
  };

  const toggleEnabled = (index) => {
    const updated = [...sections];
    updated[index] = { ...updated[index], enabled: !updated[index].enabled };
    setSections(updated);
    setHasChanges(true);
  };

  const resetDefaults = () => {
    setSections([...DEFAULT_SECTIONS]);
    setHasChanges(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await updateLandingLayout(sections);
      toast.success('Layout del landing guardado correctamente');
      setHasChanges(false);
    } catch (error) {
      console.error('Error saving layout:', error);
      toast.error('Error al guardar el layout');
    } finally {
      setSaving(false);
    }
  };

  return (
    <DashboardLayout>
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Layout del Landing</h1>
            <p className="text-sm text-slate-500 mt-1">
              Reordena y activa/desactiva las secciones del marketplace. Hero y CTA son fijos.
            </p>
          </div>
          <a
            href="/"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-teal-700 bg-teal-50 rounded-lg hover:bg-teal-100 transition-colors"
          >
            <ExternalLink className="w-4 h-4" />
            Ver Landing
          </a>
        </div>

        {/* Fixed: Hero */}
        <div className="bg-slate-100 rounded-xl p-4 mb-2 flex items-center gap-3 opacity-60">
          <GripVertical className="w-5 h-5 text-slate-300" />
          <div className="flex-1">
            <span className="font-medium text-slate-700">Hero / Buscador</span>
            <span className="ml-2 text-xs text-slate-400">(siempre primero)</span>
          </div>
          <span className="text-xs px-2 py-1 bg-slate-200 rounded-full text-slate-500">Fijo</span>
        </div>

        {/* Configurable sections */}
        {loading ? (
          <div className="space-y-2">
            {[1, 2, 3, 4, 5].map(i => (
              <div key={i} className="h-16 bg-slate-100 rounded-xl animate-pulse" />
            ))}
          </div>
        ) : (
          <div className="space-y-2">
            {sections.map((section, index) => (
              <div
                key={section.section_id}
                className={`rounded-xl p-4 flex items-center gap-3 border transition-colors ${
                  section.enabled
                    ? 'bg-white border-slate-200'
                    : 'bg-slate-50 border-slate-100 opacity-60'
                }`}
              >
                {/* Drag handle visual */}
                <GripVertical className="w-5 h-5 text-slate-300 flex-shrink-0" />

                {/* Order number */}
                <span className="w-7 h-7 rounded-full bg-teal-100 text-teal-700 text-sm font-bold flex items-center justify-center flex-shrink-0">
                  {index + 1}
                </span>

                {/* Label */}
                <div className="flex-1 min-w-0">
                  <span className="font-medium text-slate-800">{section.label}</span>
                  <span className="ml-2 text-xs text-slate-400">{section.section_id}</span>
                </div>

                {/* Toggle visibility */}
                <button
                  onClick={() => toggleEnabled(index)}
                  className={`p-2 rounded-lg transition-colors ${
                    section.enabled ? 'text-teal-600 hover:bg-teal-50' : 'text-slate-400 hover:bg-slate-100'
                  }`}
                  title={section.enabled ? 'Desactivar' : 'Activar'}
                >
                  {section.enabled ? <Eye className="w-5 h-5" /> : <EyeOff className="w-5 h-5" />}
                </button>

                {/* Move up */}
                <button
                  onClick={() => moveUp(index)}
                  disabled={index === 0}
                  className="p-2 rounded-lg hover:bg-slate-100 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                  title="Mover arriba"
                >
                  <ArrowUp className="w-5 h-5 text-slate-600" />
                </button>

                {/* Move down */}
                <button
                  onClick={() => moveDown(index)}
                  disabled={index === sections.length - 1}
                  className="p-2 rounded-lg hover:bg-slate-100 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                  title="Mover abajo"
                >
                  <ArrowDown className="w-5 h-5 text-slate-600" />
                </button>
              </div>
            ))}
          </div>
        )}

        {/* Fixed: CTA */}
        <div className="bg-slate-100 rounded-xl p-4 mt-2 flex items-center gap-3 opacity-60">
          <GripVertical className="w-5 h-5 text-slate-300" />
          <div className="flex-1">
            <span className="font-medium text-slate-700">CTA Negocios</span>
            <span className="ml-2 text-xs text-slate-400">(siempre ultimo)</span>
          </div>
          <span className="text-xs px-2 py-1 bg-slate-200 rounded-full text-slate-500">Fijo</span>
        </div>

        {/* Actions */}
        <div className="flex items-center justify-between mt-6">
          <button
            onClick={resetDefaults}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-slate-600 bg-slate-100 rounded-lg hover:bg-slate-200 transition-colors"
          >
            <RotateCcw className="w-4 h-4" />
            Restablecer
          </button>

          <button
            onClick={handleSave}
            disabled={saving || !hasChanges}
            className="flex items-center gap-2 px-6 py-2.5 text-sm font-semibold text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            style={{ backgroundColor: hasChanges ? '#0d9488' : '#94a3b8' }}
          >
            <Save className="w-4 h-4" />
            {saving ? 'Guardando...' : 'Guardar'}
          </button>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default LandingLayout;
